/* eslint-disable react-hooks/exhaustive-deps */
import "../styles/globals.css";
import type { AppProps } from "next/app";
import { ApolloProvider } from "@apollo/client";
import Layout from "@common/components/Layout/Layout";
import "firebase/auth";
import { AuthProvider } from "@src/auth/components/AuthProvider";
import apolloClient from "@src/common/lib/apollo/ApolloClients";
import Script from "next/script";
import { ErrorBoundary } from "@sentry/react";
import { LayoutProvider } from "@src/common/components/Layout/contexts/LayoutContext";
import { ThemeProvider } from "@src/common/components/ThemeProvider/ThemeProvider";
import { useThemeUserPrefSync } from "@src/common/components/ThemeProvider/useThemeUserPrefSync";
import { TimeFormatProvider } from "@src/common/components/TimeFormatProvider/TimeFormatProvider";
import { useShortcutNavigation } from "@src/common/hooks/useShortcutNavigation";
import Mustache from "mustache";
import { useRouter } from "next/router";
import { useAuth } from "@src/common/hooks/useAuth";

console.warn = () => {};

// Make mustache's escape work the same as the backend's mustache.
Mustache.escape = (s) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function MyApp({ Component, pageProps }: AppProps) {
  return (
    <ErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider>
          <AuthProvider>
            <TimeFormatProvider>
              <Index>
                <Component {...pageProps} />
              </Index>
            </TimeFormatProvider>
          </AuthProvider>
        </ThemeProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
}

function Index({ children }) {
  const router = useRouter();
  const { authLoading, isAuthenticated, courierId } = useAuth();
  useThemeUserPrefSync();

  const googleMapsUrl = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_ACCESS_TOKEN}&libraries=places`;

  if (authLoading) {
    return <p>Loading</p>;
  }

  // Admin/internal pages render their own standalone layout, so they must not
  // be wrapped in the main app Layout (which would stack two navs).
  const isStandalonePage = router.pathname.startsWith("/admin/");

  return !authLoading && isAuthenticated && courierId && !isStandalonePage ? (
    <AuthenticatedApp googleMapsUrl={googleMapsUrl}>{children}</AuthenticatedApp>
  ) : (
    <UnAuthenticatedApp>{children}</UnAuthenticatedApp>
  );
}

function AuthenticatedApp({ children, googleMapsUrl }) {
  useShortcutNavigation();
  return (
    <LayoutProvider>
      <Layout>
        <Script src={googleMapsUrl} />
        {children}
      </Layout>
    </LayoutProvider>
  );
}

function UnAuthenticatedApp({ children }) {
  return <>{children}</>;
}

export default MyApp;
